import React, { useEffect, useState, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Pagination, PaginationItem, Card, TextField, Box, Stack, IconButton,
  Skeleton, Typography, Chip, InputAdornment, Fade, LinearProgress,
  Select, MenuItem, FormControl, InputLabel, Button, Tooltip, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions, Collapse, alpha,
  CircularProgress,
} from "@mui/material";
import {
  MdSearch, MdClear, MdRefresh, MdFilterList, MdEdit, MdArrowBack,
  MdArrowForward, MdInbox, MdRestore, MdExpandMore, MdExpandLess,
  MdStar,
} from "react-icons/md";
import axios from "axios";
import { toast } from "react-toastify";
import BulkPriceModal from "./BulkPriceModal";
import { getProviderMeta } from "../../../utils/labProviders";

const BASE = "https://api.medstown.com";

// Orange Health-only by design — see the note in LabTestsList.jsx. The
// OHPackage collection has no `provider` field, /admin/lab/packages accepts no
// `provider` param, and Redcliffe's catalog is served live from their API
// rather than stored here, so it has no editable per-item price row.
const PARTNER = getProviderMeta("orange_labs");

const C = {
  primary: "#0c4a6e",
  secondary: "#0284c7",
  accent: "#38bdf8",
  success: "#16a34a",
  error: "#dc2626",
  warning: "#d97706",
  custom: "#7c3aed",
  pkg: "#0e7490",
};

// ── Single price edit dialog ──────────────────────────────────────────────────
function PriceDialog({ open, onClose, item, onSaved }) {
  const [val, setVal]       = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) setVal(item.medstownPrice != null ? String(item.medstownPrice) : "");
  }, [item]);

  const mrp       = item?.mrp;
  const netRate   = item?.netRate;
  const parsed    = parseFloat(val);
  const hasVal    = val.trim() !== "" && !isNaN(parsed);
  const currentSell = item?.medstownPrice != null ? item.medstownPrice : netRate;
  const diff      = hasVal && mrp != null ? parseFloat((parsed - mrp).toFixed(2)) : null;

  async function save() {
    const price = val.trim() === "" ? null : parsed;
    if (price !== null && isNaN(price)) { toast.error("Enter a valid number"); return; }
    setSaving(true);
    try {
      await axios.patch(`${BASE}/admin/lab/packages/${item.packageId}/price`, { medstownPrice: price });
      toast.success("Price updated"); onSaved(); onClose();
    } catch { toast.error("Failed to update price"); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${C.pkg}, ${C.secondary})`,
        color: "#fff", fontWeight: 700, fontSize: "0.95rem", py: 1.5,
      }}>
        Set Medstown Price
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Typography variant="body2" fontWeight={600} mb={1.5}>{item?.name}</Typography>
        <Stack direction="row" spacing={1} mb={2}>
          {[
            { label: "MRP",      value: mrp     != null ? `₹${Number(mrp).toFixed(2)}`     : "—", color: C.primary },
            { label: "Net Rate", value: netRate  != null ? `₹${Number(netRate).toFixed(2)}` : "—", color: C.warning },
            { label: "Current",  value: currentSell != null ? `₹${Number(currentSell).toFixed(2)}` : "—", color: C.success },
          ].map(f => (
            <Box key={f.label} sx={{ flex: 1, bgcolor: alpha(f.color, 0.06), border: `1px solid ${alpha(f.color, 0.15)}`, borderRadius: 1.5, p: 1, textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ fontSize: "0.65rem", textTransform: "uppercase", display: "block" }}>{f.label}</Typography>
              <Typography fontWeight={700} sx={{ color: f.color, fontSize: "0.85rem" }}>{f.value}</Typography>
            </Box>
          ))}
        </Stack>

        <TextField
          fullWidth size="small" type="number"
          label="Medstown Selling Price (₹)"
          placeholder={`Blank = reset to Net Rate (₹${netRate != null ? Number(netRate).toFixed(2) : "—"})`}
          value={val} onChange={e => setVal(e.target.value)}
          sx={{ mb: hasVal ? 1.5 : 0, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.85rem" } }}
          helperText="Override selling price. Blank = reset to Net Rate."
          FormHelperTextProps={{ sx: { fontSize: "0.68rem" } }}
        />

        {hasVal && diff != null && (
          <Fade in>
            <Box sx={{
              p: "8px 12px", borderRadius: 1.5,
              bgcolor: diff > 0 ? alpha(C.warning, 0.08) : alpha(C.success, 0.08),
              border: `1px solid ${diff > 0 ? alpha(C.warning, 0.25) : alpha(C.success, 0.25)}`,
            }}>
              <Typography variant="caption" fontWeight={600} sx={{ color: diff > 0 ? C.warning : C.success, fontSize: "0.75rem" }}>
                {diff > 0 ? `+₹${diff} above MRP` : diff < 0 ? `-₹${Math.abs(diff)} below MRP` : "Same as MRP"}
                {netRate != null && ` · Margin: ₹${(parsed - netRate).toFixed(2)}`}
              </Typography>
            </Box>
          </Fade>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ borderRadius: 1.5, fontSize: "0.78rem", color: "text.secondary" }}>Cancel</Button>
        <Button size="small" variant="contained" onClick={save} disabled={saving}
          sx={{ borderRadius: 1.5, fontSize: "0.78rem", background: `linear-gradient(135deg, ${C.pkg}, ${C.secondary})` }}>
          {saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Internal Cost (netRate) edit dialog ───────────────────────────────────────
function CostDialog({ open, onClose, item, onSaved }) {
  const [val, setVal]       = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) setVal(item.netRate != null ? String(item.netRate) : "");
  }, [item]);

  const parsed = parseFloat(val);
  const hasVal = val.trim() !== "" && !isNaN(parsed);

  async function save() {
    if (!hasVal) { toast.error("Enter a valid number"); return; }
    setSaving(true);
    try {
      await axios.patch(`${BASE}/admin/lab/packages/${item.packageId}/cost`, { netRate: parsed });
      toast.success("Internal cost updated");
      onSaved(); onClose();
    } catch { toast.error("Failed to update cost"); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${C.warning}, #b45309)`,
        color: "#fff", fontWeight: 700, fontSize: "0.95rem", py: 1.5,
      }}>
        Update Internal Cost
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Typography variant="body2" fontWeight={600} mb={0.5}>{item?.name}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2, fontSize: "0.72rem" }}>
          This is what Medstown pays {PARTNER.label} (netRate). Updating keeps margin calculations accurate.
        </Typography>
        <TextField
          fullWidth size="small" type="number"
          label="Internal Cost / Net Rate (₹)"
          value={val} onChange={e => setVal(e.target.value)}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.85rem" } }}
        />
        {hasVal && item?.mrp != null && (
          <Fade in>
            <Box sx={{ mt: 1.5, p: "8px 12px", borderRadius: 1.5, bgcolor: alpha(C.warning, 0.07), border: `1px solid ${alpha(C.warning, 0.2)}` }}>
              <Typography variant="caption" fontWeight={600} sx={{ color: C.warning, fontSize: "0.72rem" }}>
                MRP: ₹{Number(item.mrp).toFixed(2)} · New cost: ₹{parsed.toFixed(2)} · Margin: ₹{(item.mrp - parsed).toFixed(2)}
              </Typography>
            </Box>
          </Fade>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ borderRadius: 1.5, fontSize: "0.78rem", color: "text.secondary" }}>Cancel</Button>
        <Button size="small" variant="contained" onClick={save} disabled={saving || !hasVal}
          sx={{ borderRadius: 1.5, fontSize: "0.78rem", bgcolor: C.warning, "&:hover": { bgcolor: "#b45309" } }}>
          {saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Expandable tests sub-row ──────────────────────────────────────────────────
function PackageRow({ row, selected, onSelect, onEdit, onReset, onEditCost }) {
  const [open, setOpen] = useState(false);

  const selling  = row.medstownPrice != null ? row.medstownPrice : row.netRate;
  const isCustom = row.medstownPrice != null;
  const diff     = row.netRate != null ? parseFloat((selling - row.netRate).toFixed(2)) : null;

  return (
    <>
      <TableRow hover selected={selected}
        sx={{
          "&:hover": { backgroundColor: alpha(C.pkg, 0.025) },
          "&.Mui-selected": { backgroundColor: alpha(C.secondary, 0.06) },
          borderBottom: open ? "none" : `1px solid ${alpha(C.primary, 0.05)}`,
        }}>
        <TableCell padding="checkbox" sx={{ pl: "14px !important" }}>
          <Checkbox size="small" checked={selected} onChange={() => onSelect(row.packageId)}
            sx={{ p: 0.3, color: C.secondary, "&.Mui-checked": { color: C.secondary } }} />
        </TableCell>

        <TableCell>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton size="small" onClick={() => setOpen(o => !o)}
              sx={{ p: 0.3, color: alpha(C.pkg, 0.6) }}>
              {open ? <MdExpandLess size={15} /> : <MdExpandMore size={15} />}
            </IconButton>
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.4}>
                {row.isFeatured && <MdStar size={12} color="#f59e0b" />}
                <Typography fontWeight={600} noWrap sx={{ maxWidth: 200, fontSize: "0.78rem" }}>{row.name}</Typography>
              </Stack>
              <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.65rem", color: alpha(C.pkg, 0.5) }}>
                ID: {row.packageId} · {row.tests?.length || 0} tests
              </Typography>
            </Box>
          </Stack>
        </TableCell>

        <TableCell align="right">
          <Typography sx={{
            fontSize: "0.78rem",
            textDecoration: isCustom ? "line-through" : "none",
            color: isCustom ? "text.disabled" : "text.primary",
          }}>
            {row.mrp != null ? `₹${Number(row.mrp).toFixed(2)}` : "—"}
          </Typography>
        </TableCell>

        <TableCell align="right">
          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.3}>
            <Typography sx={{ fontSize: "0.78rem", color: C.warning, fontWeight: 600 }}>
              {row.netRate != null ? `₹${Number(row.netRate).toFixed(2)}` : "—"}
            </Typography>
            <Tooltip title="Edit internal cost">
              <IconButton size="small" onClick={() => onEditCost(row)}
                sx={{ p: 0.2, color: alpha(C.warning, 0.6), "&:hover": { color: C.warning, bgcolor: alpha(C.warning, 0.08) } }}>
                <MdEdit size={11} />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>

        <TableCell align="right">
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: isCustom ? C.custom : "text.primary" }}>
            {selling != null ? `₹${Number(selling).toFixed(2)}` : "—"}
          </Typography>
        </TableCell>

        <TableCell align="right">
          {isCustom && diff != null ? (
            <Box component="span" sx={{
              fontSize: "0.68rem", fontWeight: 700, px: 0.6, py: 0.2, borderRadius: 0.8,
              bgcolor: diff > 0 ? alpha(C.warning, 0.1) : alpha(C.success, 0.1),
              color: diff > 0 ? C.warning : C.success,
            }}>
              {diff > 0 ? `+₹${diff}` : `-₹${Math.abs(diff)}`}
            </Box>
          ) : (
            <Typography sx={{ fontSize: "0.68rem", color: "text.disabled" }}>—</Typography>
          )}
        </TableCell>

        <TableCell align="center">
          <Typography variant="caption" sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
            {row.tatHours != null ? `${row.tatHours}h` : row.tatLabel || "NA"}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Stack direction="row" spacing={0.3} justifyContent="center">
            <Tooltip title="Edit price">
              <IconButton size="small" onClick={() => onEdit(row)}
                sx={{ p: 0.4, color: C.pkg, "&:hover": { bgcolor: alpha(C.pkg, 0.08) } }}>
                <MdEdit size={14} />
              </IconButton>
            </Tooltip>
            {isCustom && (
              <Tooltip title="Reset to original">
                <IconButton size="small" onClick={() => onReset(row)}
                  sx={{ p: 0.4, color: C.warning, "&:hover": { bgcolor: alpha(C.warning, 0.1) } }}>
                  <MdRestore size={14} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {/* Expanded test chips */}
      <TableRow sx={{ "& .MuiTableCell-root": { py: 0, border: 0 } }}>
        <TableCell colSpan={8} sx={{ p: 0 }}>
          <Collapse in={open}>
            <Box sx={{ bgcolor: alpha(C.pkg, 0.03), px: 4, py: 1.5, borderBottom: `1px solid ${alpha(C.pkg, 0.08)}` }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: C.pkg, fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.4px", display: "block", mb: 0.8 }}>
                Included Tests ({row.tests?.length || 0})
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {(row.tests || []).map((t, i) => (
                  <Chip key={i} label={t} size="small" sx={{
                    height: 20, fontSize: "0.65rem",
                    bgcolor: alpha(C.pkg, 0.07),
                    color: C.pkg,
                    border: `1px solid ${alpha(C.pkg, 0.15)}`,
                    "& .MuiChip-label": { px: 0.8 },
                  }} />
                ))}
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function LabPackagesList() {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [page, setPage]         = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch]     = useState("");
  const [debSearch, setDebSearch] = useState("");
  const [priceFilter, setPriceFilter] = useState("");
  const [selected, setSelected] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [costItem, setCostItem] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setDebSearch(search); setPage(1); }, 450);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams({ page, limit: 20 });
      if (debSearch)   p.append("q", debSearch);
      if (priceFilter) p.append("priceFilter", priceFilter);
      const { data } = await axios.get(`${BASE}/admin/lab/packages?${p}`);
      setRows(data.packages || []);
      setTotalPages(data.pagination?.pages || 1);
      setTotalCount(data.pagination?.total || 0);
      setSelected([]);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load packages");
    } finally { setLoading(false); }
  }, [page, debSearch, priceFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasFilters = search || priceFilter;
  function clearFilters() { setSearch(""); setPriceFilter(""); setPage(1); }

  function toggleRow(id) {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  }
  function toggleAll() {
    const ids = rows.map(r => r.packageId);
    setSelected(s => s.length === ids.length ? [] : ids);
  }

  async function handleReset(row) {
    try {
      await axios.patch(`${BASE}/admin/lab/packages/${row.packageId}/price`, { medstownPrice: null });
      toast.success("Reset to original price");
      fetchData();
    } catch { toast.error("Failed"); }
  }

  const allSelected = rows.length > 0 && selected.length === rows.length;
  const smallSx = { height: 32, fontSize: "0.78rem", borderRadius: 2 };

  return (
    <Card sx={{
      borderRadius: 2.5,
      boxShadow: `0 2px 12px ${alpha("#000", 0.06)}`,
      border: `1px solid ${alpha(C.pkg, 0.12)}`,
      overflow: "hidden", position: "relative",
    }}>
      {loading && (
        <LinearProgress sx={{
          position: "absolute", top: 0, left: 0, right: 0, zIndex: 10, height: 2,
          "& .MuiLinearProgress-bar": { background: `linear-gradient(90deg, ${C.pkg}, ${C.secondary})` },
        }} />
      )}

      {/* Filters */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.5, flexWrap: "wrap", gap: 1 }}>
        <TextField size="small" placeholder="Search packages…" value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{
            width: 220,
            "& .MuiOutlinedInput-root": { height: 32, fontSize: "0.8rem", borderRadius: 2, "& fieldset": { borderColor: alpha(C.pkg, 0.2) } },
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><MdSearch size={15} color={C.pkg} /></InputAdornment>,
            endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0.3 }}><MdClear size={14} /></IconButton></InputAdornment> : null,
          }}
        />

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel sx={{ fontSize: "0.78rem" }}>Price</InputLabel>
          <Select value={priceFilter} label="Price" onChange={e => { setPriceFilter(e.target.value); setPage(1); }} sx={smallSx}>
            <MenuItem value="" dense>All</MenuItem>
            <MenuItem value="custom" dense>Custom Priced</MenuItem>
            <MenuItem value="default" dense>Default (Net Rate)</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flex: 1 }} />

        {selected.length > 0 && (
          <Button size="small" variant="contained"
            onClick={() => setBulkOpen(true)}
            sx={{
              height: 30, fontSize: "0.75rem", borderRadius: 1.5, textTransform: "none",
              background: `linear-gradient(135deg, ${C.custom}, #9333ea)`,
              boxShadow: `0 4px 12px ${alpha(C.custom, 0.3)}`,
            }}>
            Bulk ({selected.length})
          </Button>
        )}

        {hasFilters && (
          <Tooltip title="Clear filters">
            <IconButton size="small" onClick={clearFilters} sx={{ p: 0.5, color: C.error }}>
              <MdFilterList size={16} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchData} sx={{ p: 0.5, color: C.pkg }}>
            <MdRefresh size={16} />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Fade in>
          <Box sx={{ mx: 1.5, mb: 1, p: "6px 12px", bgcolor: alpha(C.error, 0.07), borderRadius: 1.5, border: `1px solid ${alpha(C.error, 0.2)}` }}>
            <Typography variant="caption" color="error">{error}</Typography>
          </Box>
        </Fade>
      )}

      {/* Table */}
      <TableContainer>
        <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.75, px: 1.2, fontSize: "0.78rem" } }}>
          <TableHead>
            <TableRow sx={{
              "& .MuiTableCell-head": {
                backgroundColor: alpha(C.pkg, 0.05), fontWeight: 700,
                fontSize: "0.65rem", color: C.pkg, border: "none",
                py: 0.9, textTransform: "uppercase", letterSpacing: "0.5px",
              },
            }}>
              <TableCell padding="checkbox" sx={{ pl: "14px !important" }}>
                <Checkbox size="small" checked={allSelected}
                  indeterminate={selected.length > 0 && !allSelected}
                  onChange={toggleAll}
                  sx={{ p: 0.3, color: C.pkg, "&.Mui-checked": { color: C.pkg } }} />
              </TableCell>
              <TableCell>Package</TableCell>
              <TableCell align="right">MRP</TableCell>
              <TableCell align="right">Net Rate</TableCell>
              <TableCell align="right">Medstown Price</TableCell>
              <TableCell align="right">Margin</TableCell>
              <TableCell align="center">TAT</TableCell>
              <TableCell align="center">•••</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !rows.length ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 8 }).map((_, j) => (
                    <TableCell key={j}><Skeleton height={18} sx={{ borderRadius: 1 }} /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                  <MdInbox size={28} color={alpha(C.pkg, 0.25)} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: "0.78rem" }}>
                    {hasFilters ? "No results — try different filters" : "No packages found"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.map(row => (
              <PackageRow
                key={row.packageId}
                row={row}
                selected={selected.includes(row.packageId)}
                onSelect={toggleRow}
                onEdit={setEditItem}
                onReset={handleReset}
                onEditCost={setCostItem}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.5, py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {rows.length} of {totalCount} packages
        </Typography>
        {totalPages > 1 && (
          <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)}
            size="small" shape="rounded"
            renderItem={item => (
              <PaginationItem slots={{ previous: MdArrowBack, next: MdArrowForward }} {...item}
                sx={{
                  fontSize: "0.75rem", minWidth: 28, height: 28, borderRadius: 1.5,
                  "&.Mui-selected": { backgroundColor: C.pkg, color: "#fff" },
                }} />
            )} />
        )}
      </Stack>

      <PriceDialog open={!!editItem} onClose={() => setEditItem(null)} item={editItem} onSaved={fetchData} />
      <CostDialog  open={!!costItem} onClose={() => setCostItem(null)} item={costItem} onSaved={fetchData} />

      <BulkPriceModal
        open={bulkOpen} onClose={() => setBulkOpen(false)}
        selectedIds={selected} type="package" onSaved={fetchData}
      />
    </Card>
  );
}
